/**
 * THE WIRE - Types and Constants
 * Matches Python models from orioles-pipeline/models/analysis_result.py
 * For opponent pitcher scouting and pitch tipping detection
 */

import {
  getMLBHeadshotUrl,
  getHeadshotByName,
  MLB_PITCHER_REGISTRY,
  HEADSHOT_SIZES,
} from './mlbHeadshotService';

// =============================================================================
// CONFIDENCE LEVELS
// =============================================================================

export const CONFIDENCE_THRESHOLDS = {
  CRITICAL: 0.85,  // >= 85%
  HIGH: 0.70,      // 70-85%
  MEDIUM: 0.50,    // 50-70%
  LOW: 0.0,        // < 50%
};

export function getConfidenceLevel(confidence) {
  if (confidence >= 0.85) return 'CRITICAL';
  if (confidence >= 0.70) return 'HIGH';
  if (confidence >= 0.50) return 'MEDIUM';
  if (confidence > 0) return 'LOW';
  return 'NONE';
}

export function getConfidenceColor(level) {
  const colors = {
    CRITICAL: '#ef4444', // Red
    HIGH: '#f97316',     // Orange
    MEDIUM: '#eab308',   // Yellow
    LOW: '#22c55e',      // Green
    NONE: '#3b82f6',     // Blue
  };
  return colors[level] || '#6b7280';
}

// =============================================================================
// PITCH TYPES
// =============================================================================

export const PITCH_TYPE_LABELS = {
  FB: 'Fastball (4-seam)',
  SI: 'Sinker (2-seam)',
  SL: 'Slider',
  CB: 'Curveball',
  CH: 'Changeup',
  CT: 'Cutter',
  KC: 'Knuckle Curve',
};

export const PITCH_TYPE_SHORT = {
  FB: '4SFB',
  SI: 'SI',
  SL: 'SL',
  CB: 'CB',
  CH: 'CH',
  CT: 'CT',
  KC: 'KC',
};

// =============================================================================
// PITCHER INFO (Static config)
// =============================================================================

/**
 * Tigers pitchers with MLB IDs for dynamic headshot generation.
 * MLB IDs are the golden source from Statcast/Baseball Savant.
 *
 * Headshot URL generation:
 * - getMLBHeadshotUrl(mlbId) - Direct by ID
 * - getHeadshotByName('skubal') - By name lookup
 */
export const TIGERS_PITCHERS = {
  Skubal: {
    ...MLB_PITCHER_REGISTRY.skubal,
    arsenal: ['FB', 'SL', 'CH', 'CB'],
    primaryPitch: 'FB',
    notes: 'Elite slider, 2024 AL Cy Young. Watch for glove height variance on breaking balls.',
    // Dynamic headshot from MLB CDN
    get imageUrl() {
      return getMLBHeadshotUrl(this.mlbId, { size: 'MEDIUM' });
    },
    get imageUrlLarge() {
      return getMLBHeadshotUrl(this.mlbId, { size: 'LARGE' });
    },
    get imageUrlSilo() {
      return getMLBHeadshotUrl(this.mlbId, { size: 'MEDIUM', variant: 'SILO' });
    },
    // Fallback to local if CDN unavailable
    localImageUrl: '/intel/pitchers/skubal.jpg',
  },
  Flaherty: {
    ...MLB_PITCHER_REGISTRY.flaherty,
    fullName: 'Jack Flaherty',
    arsenal: ['FB', 'SL', 'CB', 'CT'],
    primaryPitch: 'FB',
    notes: 'Former Cardinal, strong cutter. Watch for tempo changes between fastball and breaking pitches.',
    get imageUrl() {
      return getMLBHeadshotUrl(this.mlbId, { size: 'MEDIUM' });
    },
    get imageUrlLarge() {
      return getMLBHeadshotUrl(this.mlbId, { size: 'LARGE' });
    },
    get imageUrlSilo() {
      return getMLBHeadshotUrl(this.mlbId, { size: 'MEDIUM', variant: 'SILO' });
    },
    localImageUrl: '/intel/pitchers/flaherty.jpg',
  },
  Mize: {
    ...MLB_PITCHER_REGISTRY.mize,
    arsenal: ['FB', 'SL', 'SI', 'CH'],
    primaryPitch: 'SI',
    notes: 'Sinker-slider pitcher. Focus on arm slot consistency between sinker and slider.',
    get imageUrl() {
      return getMLBHeadshotUrl(this.mlbId, { size: 'MEDIUM' });
    },
    get imageUrlLarge() {
      return getMLBHeadshotUrl(this.mlbId, { size: 'LARGE' });
    },
    get imageUrlSilo() {
      return getMLBHeadshotUrl(this.mlbId, { size: 'MEDIUM', variant: 'SILO' });
    },
    localImageUrl: '/intel/pitchers/mize.jpg',
  },
};

// =============================================================================
// COLLEGE PITCHERS
// =============================================================================

/**
 * College pitchers for scouting analysis.
 * Wake Forest and other NCAA teams.
 */
export const COLLEGE_PITCHERS = {
  'Wake Forest Pitcher': {
    name: 'Wake Forest Pitcher',
    fullName: 'Wake Forest Pitcher',
    team: 'Wake Forest',
    league: 'ACC',
    throws: 'R',
    arsenal: ['FB', 'SL', 'CB', 'CH'],
    primaryPitch: 'FB',
    notes: 'College pitcher analysis from multi-angle video. High tipping rate on off-speed pitches.',
    imageUrl: '/logos/wake-forest.svg',
    localImageUrl: '/logos/wake-forest.svg',
  },
};

// =============================================================================
// INTEL SOURCE CONFIGURATION
// =============================================================================

/**
 * Available intel sources with team-specific configurations.
 */
export const INTEL_SOURCES = {
  tigers: {
    id: 'tigers',
    name: 'Detroit Tigers',
    shortName: 'Tigers',
    league: 'MLB',
    path: '',  // Default path (root of /intel/)
    pitchers: TIGERS_PITCHERS,
    logo: '/logos/MLB/detroit-tigers.png',
    bucketName: 'slam-pitch-video',
    isMLB: true,
    hasPublicVideos: true,  // GCS bucket has public access
  },
  'wake-forest': {
    id: 'wake-forest',
    name: 'Wake Forest',
    shortName: 'Wake Forest',
    league: 'ACC (College)',
    path: 'wake-forest/',  // Subdirectory of /intel/
    pitchers: COLLEGE_PITCHERS,
    logo: '/logos/CBB/Athletic-WF.png',
    bucketName: 'slam-wf-pitch-video',
    isMLB: false,
    hasPublicVideos: true,  // Enable video thumbnails
  },
};

// Re-export headshot utilities for convenience
export { getMLBHeadshotUrl, getHeadshotByName, HEADSHOT_SIZES };

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Normalize pitcher name for consistent lookup.
 * Handles multi-word names like "Wake Forest Pitcher".
 */
export function normalizePitcherName(name) {
  if (!name) return 'Unknown';
  // Capitalize each word for proper name matching
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get pitcher info from TIGERS_PITCHERS or COLLEGE_PITCHERS with case-insensitive lookup.
 */
export function getPitcherInfo(pitcherName) {
  const normalized = normalizePitcherName(pitcherName);
  // Check MLB pitchers first, then college pitchers
  return TIGERS_PITCHERS[normalized] || COLLEGE_PITCHERS[normalized];
}

/**
 * Get tipping risk level from tipping rate
 */
export function getTippingRiskLevel(tippingRate) {
  if (tippingRate >= 0.35) return 'CRITICAL';
  if (tippingRate >= 0.25) return 'HIGH';
  if (tippingRate >= 0.15) return 'MEDIUM';
  if (tippingRate > 0) return 'LOW';
  return 'NONE';
}
